/**
 * The chance a side covers its frozen line from where the game stands now --
 * the Picks page's live "% to cover". DISPLAY ONLY: nothing ranks, sides or
 * scores on it (CLAUDE.md: picks rank on `winProbability`).
 *
 * The rest of the game's margin is read as normal, centred on the share of the
 * market's expected margin still to be played and spread by MARGIN_SIGMA
 * scaled to the time left (the same 13.5 `edge.js` uses for a full game):
 *
 *   R ~ Normal(marketMargin * f, 13.5 * sqrt(f)),  f = minutes left / 60
 *
 * The side covers when its cushion (current margin + frozen line) plus R is
 * above zero. Margins are whole points, so the boundary is read with a
 * continuity correction: a half-point line covers on R > -cushion, an integer
 * line on R > -cushion + 0.5 -- landing exactly on the number is a push, and a
 * push scores nothing in this pool, so it is never counted as half a win.
 *
 * Deliberately simple: no key-number lattice, no possession, field position or
 * timeouts. Before kickoff the page shows the board's own `winProbability`
 * instead of this.
 */

const MARGIN_SIGMA = 13.5;
const REGULATION_MINUTES = 60;
const PERIOD_MINUTES = 15;
// Overtime has no fixed length the model could use; once regulation is over a
// small floor keeps the spread from collapsing before the final whistle.
const OVERTIME_FLOOR_MINUTES = 1;

// Abramowitz-Stegun 7.1.26 erf, |error| < 1.5e-7 -- plenty for a display %.
export function normalCdf(z) {
  const t = 1 / (1 + (0.3275911 * Math.abs(z)) / Math.SQRT2);
  const y =
    1 -
    ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) *
      t *
      Math.exp((-z * z) / 2);
  return z >= 0 ? (1 + y) / 2 : (1 - y) / 2;
}

/** Minutes left in the game from ESPN's period and seconds left in it. */
export function minutesLeft(period, clockSeconds) {
  if (period == null || clockSeconds == null) return null;
  const inPeriod = Math.max(0, Number(clockSeconds)) / 60;
  if (period <= 4) return (4 - period) * PERIOD_MINUTES + inPeriod;
  return Math.max(inPeriod, OVERTIME_FLOOR_MINUTES);
}

/** Minutes played, for placing a point on the cover chart's x axis. */
export function minutesPlayed(period, clockSeconds) {
  if (period == null || clockSeconds == null) return null;
  const elapsedInPeriod = PERIOD_MINUTES - Math.max(0, Number(clockSeconds)) / 60;
  if (period <= 4) return (period - 1) * PERIOD_MINUTES + elapsedInPeriod;
  return REGULATION_MINUTES;
}

/**
 * P(cover) for a side `cushion` points past its frozen `line` (positive is
 * inside) with `minutes` left, when the market expected the side to win by
 * `marketMargin` over a full game. `minutes <= 0` is the final whistle.
 */
export function coverChance({ cushion, line, marketMargin = 0, minutes }) {
  if (cushion == null || minutes == null || Number.isNaN(cushion)) return null;
  if (minutes <= 0) return cushion > 0 ? 1 : 0;

  const f = Math.min(minutes, REGULATION_MINUTES) / REGULATION_MINUTES;
  const mean = (marketMargin || 0) * f;
  const sd = MARGIN_SIGMA * Math.sqrt(f);
  const integerLine = Number.isInteger(Number(line));
  const threshold = -cushion + (integerLine ? 0.5 : 0);
  return 1 - normalCdf((threshold - mean) / sd);
}

/** "47%", clamped to 1-99 so a live game never reads as decided. */
export function formatChance(p) {
  if (p == null) return null;
  return `${Math.max(1, Math.min(99, Math.round(p * 100)))}%`;
}

function sideMargin(side, homeScore, awayScore) {
  return side === "home" ? homeScore - awayScore : awayScore - homeScore;
}

/**
 * Where a side stands right now: cushion against its frozen line, the live
 * chance, and the chance if either team scores a touchdown next (same clock).
 * Null unless the game is in progress with a score, a clock and a line.
 */
export function liveCover({ side, line, homeScore, awayScore, period, clockSeconds, marketMargin }) {
  if (line == null || homeScore == null || awayScore == null) return null;
  const minutes = minutesLeft(period, clockSeconds);
  if (minutes == null) return null;

  const cushion = sideMargin(side, homeScore, awayScore) + line + 0;
  const chance = (c) => coverChance({ cushion: c, line, marketMargin, minutes });
  return {
    cushion,
    minutes,
    p: chance(cushion),
    ifSideScores: chance(cushion + 7),
    ifOpponentScores: chance(cushion - 7),
  };
}

/**
 * The cover chance through the game, for the chart and sparklines: the
 * pre-game number at kickoff, the chance after each score in `plays`
 * (`{period, clock_seconds, home_score, away_score}`, ESPN's order), then the
 * current point while live or the result once final.
 *
 * Returns `[{ minute, p }]` with `minute` in 0-60, or null without plays.
 */
export function coverPath({ side, line, marketMargin, pregame, plays, final = false, now = null }) {
  if (!Array.isArray(plays) || line == null) return null;

  const points = [{ minute: 0, p: pregame ?? coverChance({ cushion: line, line, marketMargin, minutes: 60 }) }];
  for (const play of plays) {
    const minutes = minutesLeft(play.period, play.clock_seconds);
    const minute = minutesPlayed(play.period, play.clock_seconds);
    if (minutes == null || minute == null) continue;
    const cushion = sideMargin(side, play.home_score, play.away_score) + line + 0;
    // A score at 0:00 of the 4th is the final margin unless overtime follows,
    // so it is read with the overtime floor rather than as decided.
    points.push({ minute, p: coverChance({ cushion, line, marketMargin, minutes: Math.max(minutes, OVERTIME_FLOOR_MINUTES / 2) }) });
  }

  if (final && now?.cushion != null) {
    points.push({ minute: REGULATION_MINUTES, p: now.cushion > 0 ? 1 : 0 });
  } else if (now?.p != null && now.minute != null && now.minute >= points[points.length - 1].minute) {
    // ESPN's scoring plays can be fresher than our last score poll; a "now"
    // older than the latest play would draw the line back on itself.
    points.push({ minute: now.minute, p: now.p });
  }
  return points;
}

/** The lowest and highest chance a finished pick had before the result. */
export function pathRange(points) {
  if (!points || points.length < 2) return null;
  const during = points.slice(0, -1).map((point) => point.p);
  return { low: Math.min(...during), high: Math.max(...during) };
}
